import { createSelector } from 'reselect'
import moment from 'moment'
import config from 'constants/config'
import {
  findPostPurchaseCheckout,
  isBedbankAccommodationItemView,
  isBedbankItem,
  isInstantBookingLEHotelItem,
  isLEHotelItem,
  isNoProtectionInsuranceItem,
} from 'lib/checkout/checkoutUtils'
import { isGroupPaymentLeadOnlyCartItem } from 'checkout/lib/groupPayment/leadOnlyItems'
import { isGroupPaymentFreeExtraCartItem } from 'checkout/lib/groupPayment/freeExtraItems'
import { isLEHotel } from 'lib/offer/offerTypes'
import { buildAvailableRateKey } from 'lib/offer/availabilityUtils'
import { isPackageEligibleForZeroReservation } from 'lib/offer/reserveForZeroUtils'
import { meetsLppCancellationWindow } from 'lib/offer/cancellationPolicyUtils'
import { getAllOffers } from 'selectors/offerSelectors'
import { selectIsGuestCheckoutMode } from 'selectors/checkoutSelectors'
import { checkoutAccommodationOfferView } from 'checkout/selectors/view/accommodation'
import { getCartHasPropertyCollectItem } from '../propertyCollectSelectors'
import {
  GROUP_PAYMENT_FREE_CANCELLATION_BUFFER_DAYS,
  GROUP_PAYMENT_RESIDUAL_DUE_DAYS,
} from 'checkout/lib/groupPayment/groupPaymentDueDate'

const isLEGroupPaymentEligible = createSelector(
  (state: App.State) => state.checkout.cart.items,
  (state: App.State) => getAllOffers(state),
  (state: App.State) => checkoutAccommodationOfferView(state),
  (state: App.State) => state.offer.offerAvailableRatesByOccupancy,
  (
    cartItems,
    offers,
    accommodationOfferView,
    offerAvailableRatesByOccupancy,
  ): boolean => {
    // LE hotels are split with the group; lead-only items (LuxPlus, insurance) are paid by the lead.
    // A room's free extras go with the room.
    const hotelItemIds = new Set(cartItems.filter(isLEHotelItem).map(item => item.itemId))
    const payableItems = cartItems.filter(item =>
      !isNoProtectionInsuranceItem(item) && !isGroupPaymentFreeExtraCartItem(item, hotelItemIds),
    )
    if (!payableItems.every(item => isLEHotelItem(item) || isGroupPaymentLeadOnlyCartItem(item))) {
      return false
    }

    const hotelItems = payableItems.filter(isLEHotelItem)
    if (!hotelItems.length) {
      return false
    }

    const offerIds = new Set(hotelItems.map(item => item.offerId))
    if (offerIds.size !== 1) {
      return false
    }

    const offer = offers[hotelItems[0].offerId]
    if (!isLEHotel(offer)) {
      return false
    }

    return accommodationOfferView.hasRequiredData &&
      accommodationOfferView.data.length > 0 &&
      accommodationOfferView.data.every(view => {
        const checkIn = view.startDate ? moment(view.startDate) : undefined
        return view.itemViews.length > 0 && view.itemViews.every(itemView => {
          const pkg = 'pkg' in itemView ? itemView.pkg : undefined
          // Channel-managed LPC rates carry their own free-cancel deadline on the available rate.
          const dynamicCancellationPolicies = 'pkg' in itemView && isInstantBookingLEHotelItem(itemView.item) ?
            offerAvailableRatesByOccupancy[offer.id]?.[
              buildAvailableRateKey(itemView.item.checkIn, itemView.item.checkOut, [itemView.item.occupancy])
            ]?.rates.find(rate => rate.packageUniqueKey === pkg?.uniqueKey)?.cancellationPolicies :
            undefined
          return isPackageEligibleForZeroReservation(
            offer,
            checkIn,
            pkg?.roomRate?.cancellationPolicy?.type,
            dynamicCancellationPolicies,
            pkg?.ignoreDynamicCancellationPolicy,
            GROUP_PAYMENT_RESIDUAL_DUE_DAYS + GROUP_PAYMENT_FREE_CANCELLATION_BUFFER_DAYS,
          )
        })
      })
  },
)

const isLppGroupPaymentEligible = createSelector(
  (state: App.State) => state.checkout.cart.items,
  (state: App.State) => checkoutAccommodationOfferView(state),
  (cartItems, accommodationOfferView): boolean => {
    // One bedbank room is split with the group; lead-only items (LuxPlus, insurance) are paid by the lead.
    // A room's free extras go with the room.
    const roomItemIds = new Set(cartItems.filter(isBedbankItem).map(item => item.itemId))
    const payableItems = cartItems.filter(item =>
      !isNoProtectionInsuranceItem(item) && !isGroupPaymentFreeExtraCartItem(item, roomItemIds),
    )
    if (!payableItems.every(item => isBedbankItem(item) || isGroupPaymentLeadOnlyCartItem(item))) {
      return false
    }

    const bedbankItems = payableItems.filter(isBedbankItem)
    if (bedbankItems.length !== 1) {
      return false
    }

    const [item] = bedbankItems
    if (item.isFlightBundle) {
      return false
    }

    if (!accommodationOfferView.hasRequiredData) {
      return false
    }

    // The item view's rate carries the checkout session's cancellation policies, which are what gets booked.
    const rate = accommodationOfferView.data
      .flatMap<App.Checkout.AnyItemView>(view => view.itemViews)
      .filter(isBedbankAccommodationItemView)
      .find(itemView => itemView.item.itemId === item.itemId)
      ?.rate
    if (!rate || rate.paymentMethod !== 'leCollect' || rate.occupancyPricing.length !== 1) {
      return false
    }

    // Friends are charged on the residual due day, plus a day's buffer before free cancellation ends.
    return meetsLppCancellationWindow(rate, GROUP_PAYMENT_RESIDUAL_DUE_DAYS + GROUP_PAYMENT_FREE_CANCELLATION_BUFFER_DAYS)
  },
)

const isGroupPaymentEligible = createSelector(
  () => config.GROUP_PAYMENTS_ENABLED,
  selectIsGuestCheckoutMode,
  (state: App.State) => findPostPurchaseCheckout(state.checkout.cart.mode),
  (state: App.State) => state.checkout.cart.isGift,
  getCartHasPropertyCollectItem,
  isLEGroupPaymentEligible,
  isLppGroupPaymentEligible,
  (
    groupPaymentsEnabled,
    isGuestCheckoutMode,
    postPurchase,
    isGift,
    hasPropertyCollectItem,
    isLEEligible,
    isLppEligible,
  ): boolean => {
    if (!groupPaymentsEnabled || isGuestCheckoutMode || postPurchase || isGift || hasPropertyCollectItem) {
      return false
    }

    return isLEEligible || isLppEligible
  },
)

export default isGroupPaymentEligible
